import { useState, useCallback, useEffect } from "react";
import { useUnit } from "effector-react";
import { apiService } from "@/core/api";
import { API_ENDPOINTS } from "@/core/constants/endpoints";
import { $sectorsTerms } from "@/core/store/terms";
import { organizationMappers } from "@/core/utils/organizationMappers";
import { organizationDataChanged } from "@features/settings/store";
import { sample, createEffect } from "effector";
import { showToast } from "@/core/store/app";
import { fetchUserBasicInfoFx } from "@/features/user/store";

// ... (interfaces remain the same)

interface UserPartnerResponse {
  status: string;
  partner_data: any;
  partner_members: any[];
}

const fetchUserPartnerFx = createEffect(async () => {
  const response = await apiService.get<UserPartnerResponse>(
    API_ENDPOINTS.USER.PARTNER
  );
  if (response.status === "admin") {
    const data = await apiService.get(
      `${API_ENDPOINTS.PARTNERS.PARTNER_ORG_INFO_UPDATE}/${response.partner_data.id}`
    );
    return { ...response, organizationData: data };
  }
  return { ...response, organizationData: null };
});


const deleteOrganizationFx = createEffect(async (organizationId: string) => {
  await apiService.delete(
    `${API_ENDPOINTS.PARTNERS.PARTNER_ORG_INFO_UPDATE}/${organizationId}`
  );
  showToast({
    type: "success",
    text: "Organization deleted successfully!",
    duration: 5000,
  });
  await fetchUserBasicInfoFx(); // Trigger re-fetch of user basic info
});

sample({
  clock: deleteOrganizationFx.done,
  target: fetchUserPartnerFx,
});

export const OrganizationViewModel = () => {
  const [userPartner, setUserPartner] = useState<UserPartnerResponse | null>(
    null
  );
  const [organizationData, setOrganizationData] = useState<any | null>(null);
  const loading = useUnit(fetchUserPartnerFx.pending);
  const deleteLoading = useUnit(deleteOrganizationFx.pending);
  const [error, setError] = useState<string | null>(null);
  const sectorsTerms = useUnit($sectorsTerms);
  const [sectorNames, setSectorNames] = useState<string[]>([]);
  const fetchUserPartner = useUnit(fetchUserPartnerFx);

  useEffect(() => {
    fetchUserPartner();
  }, [fetchUserPartner]);

  useEffect(() => {
    const unwatch = fetchUserPartnerFx.doneData.watch((data) => {
      setUserPartner(data);
      setOrganizationData(data.organizationData);
    });
    const unwatchError = fetchUserPartnerFx.failData.watch((error) => {
      console.error("Failed to fetch user partner data:", error);
      setError(error.message || "Failed to fetch organization data");
      setUserPartner(null);
    });

    return () => {
      unwatch();
      unwatchError();
    };
  }, []);
  
  useEffect(() => {
    if (organizationData && sectorsTerms.data.length > 0) {
      const mappedSectorNames = organizationMappers.mapSectorIdsToNames(
        organizationData.field_sectors,
        sectorsTerms.data
      );
      setSectorNames(mappedSectorNames);
    }
  }, [organizationData, sectorsTerms.data]);
  
  sample({
    clock: organizationDataChanged,
    target: fetchUserPartnerFx,
  });

  const updateOrganization = useCallback(
    async (payload: any) => {
      if (!userPartner?.partner_data?.id) {
        throw new Error("No organization ID available");
      }

      // setLoading(true); // This is now handled by fetchUserPartnerFx.pending
      setError(null);

      try {
        await apiService.patch(
          `${API_ENDPOINTS.PARTNERS.PARTNER_ORG_INFO_UPDATE}/${userPartner.partner_data.id}`,
          payload
        );

        await fetchUserPartner();
        return true;
      } catch (err: any) {
        console.error("Failed to update organization:", err);
        setError(err.message || "Failed to update organization");
        throw err;
      } finally {
        // setLoading(false);
      }
    },
    [userPartner?.partner_data?.id, fetchUserPartner]
  );

  const deleteOrganization = useCallback(async () => {
    if (!userPartner?.partner_data?.id) {
      console.error("No organization ID available to delete");
      return;
    }
    await deleteOrganizationFx(userPartner.partner_data.id);
  }, [userPartner?.partner_data?.id]);

  return {
    userPartner,
    organizationData,
    loading,
    deleteLoading,
    error,
    fetchUserPartner,
    updateOrganization,
    deleteOrganization,
    sectorNames,
  };
};
